// The boards, as a list.
//
// A grid of thumbnails looks nice and answers almost nothing: whose board is
// this, when was it last touched, what is it about. So the list is the default
// and carries the columns that get asked about — name and tags, who made it,
// who last drew on it, when — with the grid kept as a second view for when you
// are looking for a drawing you would recognise by shape.
//
// Filtering is client side on purpose. The whole workspace is a few hundred
// boards; a round trip per keystroke would be slower and the code larger.

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { thumbnailUrl } from "../api.js";
import { Avatar } from "../ui/Avatar.jsx";
import { Menu } from "../ui/Menu.jsx";
import { Grid, Rows, Search } from "../ui/icons.jsx";
import { actorName, exactTime, plural, relativeTime } from "../ui/format.js";

const VIEW_KEY = "hmc.boardView";

const readView = () => {
  try {
    return window.localStorage.getItem(VIEW_KEY) === "grid" ? "grid" : "list";
  } catch {
    return "list";
  }
};

const SORTS = {
  updated: { label: "Last edited", of: (b) => b.updatedAt ?? "", direction: -1 },
  name: { label: "Name", of: (b) => (b.name ?? "").toLowerCase(), direction: 1 },
  created: { label: "Created", of: (b) => b.createdAt ?? "", direction: -1 },
};

const matches = (board, term) => {
  if (!term) return true;
  const haystack = [
    board.name,
    board.folderName,
    ...(board.tags ?? []),
    board.createdByName,
    board.lastEditedByName,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return haystack.includes(term);
};

const creator = (board) => ({
  name: actorName(board.createdByName, board.createdByEmail, board.createdBy),
  email: board.createdByEmail,
});

const editor = (board) =>
  board.lastEditedBy
    ? actorName(board.lastEditedByName, board.lastEditedByEmail, board.lastEditedBy)
    : null;

export const BoardBrowser = ({ boards, actions = {}, showFolder = false, emptyState = null }) => {
  const [view, setView] = useState(readView);
  const [term, setTerm] = useState("");
  const [activeTags, setActiveTags] = useState([]);
  const [sort, setSort] = useState("updated");
  const [flipped, setFlipped] = useState(false);

  const chooseView = (next) => {
    setView(next);
    try {
      window.localStorage.setItem(VIEW_KEY, next);
    } catch {
      /* a view preference is not worth an error */
    }
  };

  const allTags = useMemo(
    () => [...new Set(boards.flatMap((b) => b.tags ?? []))].sort(),
    [boards],
  );

  const shown = useMemo(() => {
    const needle = term.trim().toLowerCase();
    const { of, direction } = SORTS[sort];
    const sign = direction * (flipped ? -1 : 1);
    return boards
      .filter((b) => matches(b, needle))
      .filter((b) => activeTags.every((tag) => (b.tags ?? []).includes(tag)))
      .slice()
      .sort((a, b) => {
        const left = of(a);
        const right = of(b);
        return left < right ? -sign : left > right ? sign : 0;
      });
  }, [boards, term, activeTags, sort, flipped]);

  const toggleTag = (tag) =>
    setActiveTags((current) =>
      current.includes(tag) ? current.filter((t) => t !== tag) : [...current, tag],
    );

  const sortBy = (key) => {
    if (key === sort) setFlipped((f) => !f);
    else {
      setSort(key);
      setFlipped(false);
    }
  };

  const mark = (key) => (sort === key ? <span className="sort-mark">{flipped ? "↑" : "↓"}</span> : null);

  if (boards.length === 0 && emptyState) return emptyState;

  return (
    <>
      <div className="toolbar">
        <div className="search">
          <Search />
          <input
            className="input"
            type="search"
            value={term}
            placeholder="Search boards"
            aria-label="Search boards"
            onChange={(event) => setTerm(event.target.value)}
          />
        </div>

        {allTags.length > 0 ? (
          <div className="tags">
            {allTags.map((tag) => (
              <button
                key={tag}
                type="button"
                className={activeTags.includes(tag) ? "tag on" : "tag"}
                aria-pressed={activeTags.includes(tag)}
                onClick={() => toggleTag(tag)}
              >
                <span className="sq" />
                {tag}
              </button>
            ))}
          </div>
        ) : null}

        <span className="spacer" />
        <span className="muted small nowrap">{plural(shown.length, "board")}</span>
        <div className="view-toggle" role="group" aria-label="How to show the boards">
          <button
            type="button"
            className={view === "list" ? "on" : undefined}
            aria-pressed={view === "list"}
            title="List"
            onClick={() => chooseView("list")}
          >
            <Rows style={{ width: 15, height: 15 }} />
          </button>
          <button
            type="button"
            className={view === "grid" ? "on" : undefined}
            aria-pressed={view === "grid"}
            title="Grid"
            onClick={() => chooseView("grid")}
          >
            <Grid style={{ width: 15, height: 15 }} />
          </button>
        </div>
      </div>

      {shown.length === 0 ? (
        <div className="empty">
          <p className="empty-title">No matches</p>
          <p className="muted">
            {term ? `Nothing matches “${term}”.` : "No board has all of those tags."}
          </p>
          <button
            className="btn"
            onClick={() => {
              setTerm("");
              setActiveTags([]);
            }}
          >
            Clear filters
          </button>
        </div>
      ) : view === "list" ? (
        <div className="board-table">
          <div className="board-row head">
            <span />
            <span>
              <button type="button" onClick={() => sortBy("name")}>
                Board {mark("name")}
              </button>
            </span>
            <span className="col-tags">Tags</span>
            <span className="col-creator">
              <button type="button" onClick={() => sortBy("created")}>
                Created by {mark("created")}
              </button>
            </span>
            <span style={{ textAlign: "right" }}>
              <button type="button" onClick={() => sortBy("updated")}>
                Edited {mark("updated")}
              </button>
            </span>
            <span />
          </div>

          {shown.map((board) => (
            <BoardRow key={board.id} board={board} actions={actions} showFolder={showFolder} />
          ))}
        </div>
      ) : (
        <div className="board-grid">
          {shown.map((board) => (
            <BoardTile key={board.id} board={board} actions={actions} showFolder={showFolder} />
          ))}
        </div>
      )}
    </>
  );
};

const menuItems = (board, actions) =>
  [
    actions.onRename && { label: "Rename", onSelect: () => actions.onRename(board) },
    actions.onEditTags && { label: "Edit tags", onSelect: () => actions.onEditTags(board) },
    actions.onMove && { label: "Move to folder…", onSelect: () => actions.onMove(board) },
    actions.onDelete && "separator",
    actions.onDelete && { label: "Delete", danger: true, onSelect: () => actions.onDelete(board) },
  ].filter(Boolean);

const Thumb = ({ board }) =>
  board.hasThumbnail ? (
    <img src={thumbnailUrl(board.id, board.updatedAt)} alt="" loading="lazy" />
  ) : (
    <span className="empty-mark" />
  );

const BoardRow = ({ board, actions, showFolder }) => {
  const made = creator(board);
  const last = editor(board);

  return (
    <div className="board-row">
      <Link to={`/b/${board.id}`} className="board-thumb" tabIndex={-1} aria-hidden="true">
        <Thumb board={board} />
      </Link>

      <div className="board-cell">
        <Link to={`/b/${board.id}`} className="board-name truncate">
          {board.name}
        </Link>
        <div className="board-sub">
          {showFolder && board.folderName ? (
            <Link to={`/f/${board.folderId}`} className="truncate">
              {board.folderName}
            </Link>
          ) : null}
          <span>{board.elementCount ? plural(board.elementCount, "element") : "empty"}</span>
          {last && last !== made.name ? <span>last edited by {last}</span> : null}
        </div>
      </div>

      <div className="board-cell col-tags tags">
        {(board.tags ?? []).map((tag) => (
          <span className="tag" key={tag}>
            <span className="sq" />
            {tag}
          </span>
        ))}
      </div>

      <div className="board-cell col-creator who">
        <Avatar user={{ name: made.name, email: made.email }} size={22} />
        <div className="who-text truncate">
          <div className="truncate">{made.name}</div>
          <div className="small faint truncate" title={exactTime(board.createdAt)}>
            {relativeTime(board.createdAt)}
          </div>
        </div>
      </div>

      <div className="when" title={exactTime(board.updatedAt)}>
        {relativeTime(board.updatedAt)}
      </div>

      <Menu label={`Actions for ${board.name}`} items={menuItems(board, actions)} />
    </div>
  );
};

const BoardTile = ({ board, actions, showFolder }) => {
  const made = creator(board);
  return (
    <div className="board-card">
      <Link to={`/b/${board.id}`} className="board-preview">
        {board.hasThumbnail ? (
          <img src={thumbnailUrl(board.id, board.updatedAt)} alt="" loading="lazy" />
        ) : (
          <span className="board-preview-empty">
            {board.elementCount ? "no preview yet" : "empty board"}
          </span>
        )}
      </Link>
      <div className="board-meta">
        <div className="board-meta-text">
          <Link to={`/b/${board.id}`} className="board-name truncate">
            {board.name}
          </Link>
          <div className="board-sub">
            <span title={exactTime(board.updatedAt)}>{relativeTime(board.updatedAt)}</span>
            <span>· {made.name}</span>
          </div>
          {showFolder && board.folderName ? (
            <div className="board-sub">
              <Link to={`/f/${board.folderId}`}>{board.folderName}</Link>
            </div>
          ) : null}
          {(board.tags ?? []).length ? (
            <div className="tags" style={{ marginTop: 6 }}>
              {board.tags.map((tag) => (
                <span className="tag" key={tag}>
                  <span className="sq" />
                  {tag}
                </span>
              ))}
            </div>
          ) : null}
        </div>
        <Menu label={`Actions for ${board.name}`} items={menuItems(board, actions)} />
      </div>
    </div>
  );
};
